import { isEmpty, isValidByOpenApiSchemaProperty, proxy } from '@/utils'

import { countryRegionsFilterFields, getCountryRegionsFilterProperty, getCountryRegionsPatternRule } from '@/components/services/country-regions/utils'

import render from './render'

/**
 * Наблюдатели за полями фильтра: значение поля фильтра переносится в поле ввода.
 *
 * @type {Object<string, Function>}
 */
const filterFieldsWatchers = countryRegionsFilterFields.reduce((result, fieldName) => {
  result[`proxy.${fieldName}`] = function(value) {
    this.filterInputs[fieldName] = value
  }

  return result
}, {})

/**
 * Фильтр списка регионов стран по коду региона и коду страны в меню кнопки фильтра.
 * В фильтр попадает только пустое значение или значение, прошедшее проверку
 * по схеме `CountryRegionsGetManyReqQueryDtoValidator`.
 */
export default {
  name: 'Filters',

  mixins: [ proxy({ type: 'object' }) ],

  data() {
    return {
      /**
       * Введённые значения полей фильтра, включая ещё не прошедшие проверку.
       *
       * @type {Object<string, string|undefined>}
       */
      filterInputs: countryRegionsFilterFields.reduce((result, fieldName) => {
        result[fieldName] = this.value[fieldName]

        return result
      }, {})
    }
  },

  computed: {
    /**
     * Значения полей фильтра по умолчанию для кнопки сброса.
     *
     * @returns {Object<string, undefined>} Пустое значение по имени поля.
     */
    defaultFilter() {
      return countryRegionsFilterFields.reduce((result, fieldName) => {
        result[fieldName] = undefined

        return result
      }, {})
    },

    /**
     * Описания полей фильтра из схемы запроса списка.
     *
     * @returns {Object<string, object>} Описание поля по имени поля.
     */
    filterProperties() {
      return countryRegionsFilterFields.reduce((result, fieldName) => {
        result[fieldName] = getCountryRegionsFilterProperty(fieldName)

        return result
      }, {})
    }
  },

  watch: filterFieldsWatchers,

  methods: {
    /**
     * Проверяет введённое значение поля фильтра по схеме.
     *
     * @param {string} fieldName Имя поля фильтра.
     * @returns {boolean} Признак валидности введённого значения.
     */
    isFilterInputValid(fieldName) {
      return isValidByOpenApiSchemaProperty(this.filterProperties[fieldName], this.filterInputs[fieldName])
    },

    /**
     * Сохраняет введённое значение и переносит его в фильтр.
     * Пустое значение сбрасывает поле фильтра, невалидное значение в фильтр не попадает.
     *
     * @param {string} fieldName Имя поля фильтра.
     * @param {string|undefined} value Введённое значение.
     */
    setFilterInput(fieldName, value) {
      this.filterInputs[fieldName] = value
      if (isEmpty(value)) {
        this.proxy[fieldName] = undefined
      } else if (this.isFilterInputValid(fieldName)) {
        this.proxy[fieldName] = value
      }
    },

    /**
     * Принимает введённые значения полей из кнопки фильтра и переносит их в фильтр.
     *
     * @param {Object<string, string|undefined>} filterInputs Введённые значения по имени поля.
     */
    setFilterInputs(filterInputs) {
      this.filterInputs = filterInputs
      countryRegionsFilterFields.forEach(fieldName => {
        this.setFilterInput(fieldName, filterInputs[fieldName])
      })
    },

    /**
     * Возвращает состояние поля ввода фильтра.
     *
     * @param {string} fieldName Имя поля фильтра.
     * @returns {string|undefined} `error` для заполненного значения, не прошедшего проверку, иначе `undefined`.
     */
    getFilterInputState(fieldName) {
      if (!isEmpty(this.filterInputs[fieldName]) && !this.isFilterInputValid(fieldName)) {
        return 'error'
      }
    },

    /**
     * Возвращает текст ошибки поля ввода фильтра для полей без свойства `state`.
     *
     * @param {string} fieldName Имя поля фильтра.
     * @returns {string|undefined} Правило формата поля для значения, не прошедшего проверку, иначе `undefined`.
     */
    getFilterInputError(fieldName) {
      if (this.getFilterInputState(fieldName)) {
        return getCountryRegionsPatternRule(fieldName)
      }
    }
  },

  render
}
